<script lang="ts" setup>
import { QuizAnswer } from '@/models/quiz-question.model';
import { PropType, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<QuizAnswer[]>,
    required: true,
  },
});

const answers = ref<QuizAnswer[]>([
  ...(props.modelValue || []).sort((a, b) => a.index - b.index),
]);

const emit = defineEmits(['update:modelValue', 'change']);

function update() {
  emit('update:modelValue', answers.value);
  emit('change');
}

function addStep() {
  answers.value.push({ text: '', correct: false, index: answers.value.length });
  update();
}

function removeStep(index: number) {
  answers.value.splice(index, 1);
  update();
}

function moveUp(answer: QuizAnswer) {
  const index = answers.value.findIndex((a) => a.id === answer.id);

  answers.value = [
    ...answers.value.slice(0, index - 1),
    answer,
    answers.value[index - 1],
    ...answers.value.slice(index + 1),
  ].map((v, index) => ({ ...v, index }));

  update();
}

function moveDown(answer: QuizAnswer) {
  const index = answers.value.findIndex((a) => a.id === answer.id);

  answers.value = [
    ...answers.value.slice(0, index),
    answers.value[index + 1],
    answer,
    ...answers.value.slice(index + 2),
  ].map((v, index) => ({ ...v, index }));

  update();
}

watch(
  () => props.modelValue,
  () => {
    Object.assign(
      answers.value,
      (props.modelValue || []).sort((a, b) => a.index - b.index),
    );
  },
  { deep: true },
);
</script>
<template>
  <div>
    <h3>Answers</h3>
    <v-list>
      <v-list-item v-for="(answer, index) in answers" :key="index">
        <div class="answer">
          <v-text-field
            v-model="answer.text"
            label="Answer"
            :placeholder="`Answer ${index + 1}`"
            required
            @change="update"
          ></v-text-field>
          <v-checkbox v-model="answer.correct" label="Correct" />
          &nbsp;
          <v-btn
            icon="mdi-arrow-up"
            class="remove-button"
            density="compact"
            :disabled="index === 0"
            @click.prevent="moveUp(answer)"
          ></v-btn>
          &nbsp;
          <v-btn
            icon="mdi-arrow-down"
            class="remove-button"
            density="compact"
            :disabled="index === answers.length - 1"
            @click.prevent="moveDown(answer)"
          ></v-btn>
          &nbsp;
          <v-btn
            icon="mdi-delete-outline"
            class="remove-button"
            density="compact"
            @click.prevent="removeStep(index)"
          ></v-btn>
        </div>
      </v-list-item>
    </v-list>
    <v-btn @click="addStep">Add answer</v-btn>
  </div>
</template>
<style scoped>
.answer {
  display: flex;
  flex-direction: row;
  padding-right: 16px;
}
.remove-button {
  position: relative;
  top: 10px;
  left: 8px;
}
</style>
